import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Truck } from 'lucide-react';
import { api, ApiRequestError } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { errorText } from '../../../lib/crm';
import { Column, DataTable, FilterBar, FilterSelect, SearchInput } from '../../../components/DataTable';
import { Button, Field, Input, Modal, StatusBadge, Textarea, useToast } from '../../../components/ui';

const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EMPTY = { name: '', contactPerson: '', phone: '', email: '', gstin: '', address: '', paymentTerms: '', active: true };

export function VendorsTab() {
  const { can } = useAuth();
  const manage = can('crm.vendor.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [active, setActive] = useState('');
  const [editing, setEditing] = useState<any | 'new' | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<ApiRequestError | null>(null);
  const [localErr, setLocalErr] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<any | null>(null);

  const q = useQuery({
    queryKey: ['crm', 'catalog', 'vendors', { search, active }],
    queryFn: () => api.get('/crm/vendors', { q: search || undefined, active: active || undefined }),
  });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['crm', 'catalog'] }); };

  const openForm = (v: any | 'new') => {
    setEditing(v); setError(null); setLocalErr({});
    setForm(v === 'new' ? { ...EMPTY } : {
      name: v.name, contactPerson: v.contactPerson ?? '', phone: v.phone ?? '', email: v.email ?? '', gstin: v.gstin ?? '',
      address: v.address ?? '', paymentTerms: v.paymentTerms ?? '', active: v.active,
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(), contactPerson: form.contactPerson.trim() || null, phone: form.phone.trim() || null,
        email: form.email.trim() || null, gstin: form.gstin.trim().toUpperCase() || null,
        address: form.address.trim() || null, paymentTerms: form.paymentTerms.trim() || null,
      };
      return editing === 'new' ? api.post('/crm/vendors', body) : api.patch(`/crm/vendors/${editing.id}`, { ...body, active: form.active });
    },
    onSuccess: () => { toast.success('Vendor saved'); setEditing(null); refresh(); },
    onError: (e) => { if (e instanceof ApiRequestError) setError(e); toast.error('Could not save the vendor', errorText(e)); },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/crm/vendors/${removing.id}`),
    onSuccess: () => { toast.success('Vendor removed'); setRemoving(null); refresh(); },
    onError: (e) => { toast.error('Could not remove the vendor', errorText(e)); setRemoving(null); },
  });

  const submit = () => {
    const errs: Record<string, string> = {};
    if (form.name.trim().length < 2) errs.name = 'Enter the vendor name.';
    if (form.gstin.trim() && !GSTIN.test(form.gstin.trim().toUpperCase())) errs.gstin = 'Not a valid 15-character GSTIN.';
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Enter a valid email address.';
    setLocalErr(errs);
    if (!Object.keys(errs).length) save.mutate();
  };
  const fe = (k: string) => localErr[k] ?? error?.fieldError(k);
  const set = (k: keyof typeof EMPTY) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const columns: Array<Column<any>> = [
    { key: 'name', header: 'Vendor', render: (v) => (
      <div className="min-w-0"><p className="truncate font-medium text-ink-900">{v.name}</p>
        <p className="tabular text-xs text-ink-500">{v.code}{!v.active && ' · inactive'}</p></div>) },
    { key: 'contact', header: 'Contact', hideBelow: 'md', render: (v) => (
      <div className="text-xs"><p className="text-ink-800">{v.contactPerson ?? '—'}</p><p className="text-ink-500">{[v.phone, v.email].filter(Boolean).join(' · ')}</p></div>) },
    { key: 'gstin', header: 'GSTIN', hideBelow: 'lg', render: (v) => <span className="tabular text-xs">{v.gstin ?? '—'}</span> },
    { key: 'terms', header: 'Payment terms', hideBelow: 'xl', render: (v) => v.paymentTerms ?? '—' },
    { key: 'products', header: 'Products', numeric: true, render: (v) => v.productCount },
    { key: 'open', header: 'Open POs', numeric: true, render: (v) => v.openOrders > 0 ? <StatusBadge tone="progress">{v.openOrders}</StatusBadge> : '0' },
    ...(manage ? [{
      key: 'actions', header: '', render: (v: any) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" onClick={() => openForm(v)}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => setRemoving(v)}>Remove</Button>
        </div>) } as Column<any>] : []),
  ];

  return (
    <>
      <DataTable mobile="cards" columns={columns} rows={q.data?.data ?? []} rowKey={(v) => v.id}
        loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
        onRowClick={manage ? openForm : undefined}
        empty={{ icon: <Truck className="h-5 w-5" />, title: search || active ? 'No vendors match' : 'No vendors yet',
          description: 'Vendors are who we buy from when stock is short.' }}
        toolbar={
          <div className="flex flex-wrap items-end justify-between gap-2">
            <FilterBar active={[search, active].filter(Boolean).length} onReset={() => { setSearch(''); setActive(''); }}>
              <SearchInput value={search} onChange={setSearch} placeholder="Name, code or contact" />
              <FilterSelect label="Status" value={active} onChange={setActive} allLabel="Active and inactive"
                options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]} />
            </FilterBar>
            {manage && <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => openForm('new')}>New vendor</Button>}
          </div>} />

      <Modal open={!!editing} onClose={() => setEditing(null)} size="lg"
        title={editing === 'new' ? 'New vendor' : 'Edit vendor'}
        footer={<><Button onClick={() => setEditing(null)}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={submit}>Save vendor</Button></>}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vendor name" required error={fe('name')} className="sm:col-span-2"><Input value={form.name} onChange={set('name')} invalid={!!fe('name')} /></Field>
          <Field label="Contact person" error={fe('contactPerson')}><Input value={form.contactPerson} onChange={set('contactPerson')} /></Field>
          <Field label="Phone" error={fe('phone')}><Input value={form.phone} onChange={set('phone')} /></Field>
          <Field label="Email" error={fe('email')}><Input type="email" value={form.email} onChange={set('email')} invalid={!!fe('email')} /></Field>
          <Field label="GSTIN" error={fe('gstin')} hint="15 characters, e.g. 29ABCDE1234F1Z5"><Input value={form.gstin} onChange={set('gstin')} maxLength={15} invalid={!!fe('gstin')} /></Field>
          <Field label="Payment terms" error={fe('paymentTerms')} className="sm:col-span-2"><Input value={form.paymentTerms} onChange={set('paymentTerms')} placeholder="e.g. 30 days from invoice" /></Field>
          <Field label="Address" error={fe('address')} className="sm:col-span-2"><Textarea rows={2} value={form.address} onChange={set('address')} /></Field>
          {editing !== 'new' && (
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4" /> Active
            </label>)}
        </div>
      </Modal>

      <Modal open={!!removing} onClose={() => setRemoving(null)} size="sm" title="Remove vendor"
        footer={<><Button onClick={() => setRemoving(null)}>Cancel</Button><Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>Remove</Button></>}>
        <p className="text-sm text-ink-700">Remove <strong>{removing?.name}</strong>? Existing purchase history is kept. A vendor with open purchase orders cannot be removed.</p>
      </Modal>
    </>
  );
}
